import { gql } from '@apollo/client';

// GraphQL Query
export const GET_CONTACT_ADDRESSES = gql`
  query GetContactAddress($id: ID!) {
    node(id: $id) {
      ... on ContactNode {
        id
        name
        primaryAddressIds {
          id
          businessCity
          businessAddress
          businessCountryRegion
          address1County
          businessFax2
          address1Latitude
          businessStreet
          businessStreet2
          address1Street3
          address1Longitude
          businessPostalCode
          address1PostOfficeBox
          businessState
          address1Telephone1
          businessPhoneAddress
          homeCity
          homeAddress
          homeCountryRegion
          homeFax2
          homeStreet
          address2Street2
          address2Street3
          homePostalCode
          homeState
          homePhoneSecond
          address3City
          address3
          address3CountryRegion
          address3Street1
          address3Street2
          address3ZipPostalCode
          address3StateProvince
          businessPhone2
          businessFax
          homePhone2
          businessPhone
          homePhone
          otherPhone
          otherCity
          otherCountryRegion
          otherFax
          otherPhoneOld
          otherPostalCode
          otherState
          otherStreet
          geocodeRecord
        }
      }
    }
  }
`;

export type ContactAddress =
  | ContactAddress3
  | ContactAddressBusiness
  | ContactAddressHome
  | ContactAddressOther;

export interface ContactAddress3 extends ContactAddressBase {
  fullAddress: string;
  type: 'Address3';
}

// UI Data Types
export interface ContactAddressBase {
  city: string;
  countryRegion: string;
  fax: string;
  id: string;
  phone: string;
  postalCode: string;
  stateProvince: string;
  street1: string;
  street2: string;
  street3: string;
  type: ContactAddressType;
}

export interface ContactAddressBusiness extends ContactAddressBase {
  address1Telephone1: string;
  county: string;
  fax2: string;
  fullAddress: string;
  latitude: null | number;
  longitude: null | number;
  phone2: string;
  postOfficeBox: string;
  type: 'Business';
}

export interface ContactAddressesData {
  address3: ContactAddress3;
  business: ContactAddressBusiness;
  home: ContactAddressHome;
  other: ContactAddressOther;
}

export interface ContactAddressHome extends ContactAddressBase {
  fax2: string;
  fullAddress: string;
  phone2: string;
  phoneSecond: string;
  type: 'Home';
}

// API Response Types
export interface ContactAddressNode {
  address1County?: null | string;
  address1Latitude?: null | number;
  address1Longitude?: null | number;
  address1PostOfficeBox?: null | string;
  address1Street3?: null | string;
  address1Telephone1?: null | string;
  address2Street2?: null | string;
  address2Street3?: null | string;
  address3?: null | string;
  address3City?: null | string;
  address3CountryRegion?: null | string;
  address3StateProvince?: null | string;
  address3Street1?: null | string;
  address3Street2?: null | string;
  address3ZipPostalCode?: null | string;
  businessAddress?: null | string;
  businessCity?: null | string;
  businessCountryRegion?: null | string;
  businessFax?: null | string;
  businessFax2?: null | string;
  businessPhone?: null | string;
  businessPhone2?: null | string;
  businessPhoneAddress?: null | string;
  businessPostalCode?: null | string;
  businessState?: null | string;
  businessStreet?: null | string;
  businessStreet2?: null | string;
  geocodeRecord?: boolean | null;
  homeAddress?: null | string;
  homeCity?: null | string;
  homeCountryRegion?: null | string;
  homeFax2?: null | string;
  homePhone?: null | string;
  homePhone2?: null | string;
  homePhoneSecond?: null | string;
  homePostalCode?: null | string;
  homeState?: null | string;
  homeStreet?: null | string;
  id: string;
  otherCity?: null | string;
  otherCountryRegion?: null | string;
  otherFax?: null | string;
  otherPhone?: null | string;
  otherPhoneOld?: null | string;
  otherPostalCode?: null | string;
  otherState?: null | string;
  otherStreet?: null | string;
}

export interface ContactAddressOther extends ContactAddressBase {
  geocodeRecord: boolean;
  phoneOld: string;
  type: 'Other';
}

export type ContactAddressType = 'Address3' | 'Business' | 'Home' | 'Other';

export interface ContactNodeWithAddresses {
  id: string;
  name: string;
  primaryAddressIds?: ContactAddressNode | ContactAddressNode[] | null;
}

// Helper functions
export function createEmptyContactAddressesData(): ContactAddressesData {
  return {
    address3: {
      city: '',
      countryRegion: '',
      fax: '',
      fullAddress: '',
      id: '',
      phone: '',
      postalCode: '',
      stateProvince: '',
      street1: '',
      street2: '',
      street3: '',
      type: 'Address3'
    },
    business: {
      address1Telephone1: '',
      city: '',
      countryRegion: '',
      county: '',
      fax: '',
      fax2: '',
      fullAddress: '',
      id: '',
      latitude: null,
      longitude: null,
      phone: '',
      phone2: '',
      postalCode: '',
      postOfficeBox: '',
      stateProvince: '',
      street1: '',
      street2: '',
      street3: '',
      type: 'Business'
    },
    home: {
      city: '',
      countryRegion: '',
      fax: '',
      fax2: '',
      fullAddress: '',
      id: '',
      phone: '',
      phone2: '',
      phoneSecond: '',
      postalCode: '',
      stateProvince: '',
      street1: '',
      street2: '',
      street3: '',
      type: 'Home'
    },
    other: {
      city: '',
      countryRegion: '',
      fax: '',
      geocodeRecord: false,
      id: '',
      phone: '',
      phoneOld: '',
      postalCode: '',
      stateProvince: '',
      street1: '',
      street2: '',
      street3: '',
      type: 'Other'
    }
  };
}

export function mapContactAddresses(
  node: ContactAddressNode
): ContactAddressesData {
  return {
    address3: {
      city: node.address3City ?? '',
      countryRegion: node.address3CountryRegion ?? '',
      fax: '',
      fullAddress: node.address3 ?? '',
      id: node.id,
      phone: '',
      postalCode: node.address3ZipPostalCode ?? '',
      stateProvince: node.address3StateProvince ?? '',
      street1: node.address3Street1 ?? '',
      street2: node.address3Street2 ?? '',
      street3: '',
      type: 'Address3'
    },
    business: {
      address1Telephone1: node.address1Telephone1 ?? '',
      city: node.businessCity ?? '',
      countryRegion: node.businessCountryRegion ?? '',
      county: node.address1County ?? '',
      fax: node.businessFax ?? '',
      fax2: node.businessFax2 ?? '',
      fullAddress: node.businessAddress ?? '',
      id: node.id,
      latitude: node.address1Latitude ?? null,
      longitude: node.address1Longitude ?? null,
      phone: node.businessPhone ?? '',
      phone2: node.businessPhone2 ?? '',
      postalCode: node.businessPostalCode ?? '',
      postOfficeBox: node.address1PostOfficeBox ?? '',
      stateProvince: node.businessState ?? '',
      street1: node.businessStreet ?? '',
      street2: node.businessStreet2 ?? '',
      street3: node.address1Street3 ?? '',
      type: 'Business'
    },
    home: {
      city: node.homeCity ?? '',
      countryRegion: node.homeCountryRegion ?? '',
      fax: '',
      fax2: node.homeFax2 ?? '',
      fullAddress: node.homeAddress ?? '',
      id: node.id,
      phone: node.homePhone ?? '',
      phone2: node.homePhone2 ?? '',
      phoneSecond: node.homePhoneSecond ?? '',
      postalCode: node.homePostalCode ?? '',
      stateProvince: node.homeState ?? '',
      street1: node.homeStreet ?? '',
      street2: node.address2Street2 ?? '',
      street3: node.address2Street3 ?? '',
      type: 'Home'
    },
    other: {
      city: node.otherCity ?? '',
      countryRegion: node.otherCountryRegion ?? '',
      fax: node.otherFax ?? '',
      geocodeRecord: node.geocodeRecord ?? false,
      id: node.id,
      phone: node.otherPhone ?? '',
      phoneOld: node.otherPhoneOld ?? '',
      postalCode: node.otherPostalCode ?? '',
      stateProvince: node.otherState ?? '',
      street1: node.otherStreet ?? '',
      street2: '',
      street3: '',
      type: 'Other'
    }
  };
}
